import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  QueryList,
  ChangeDetectionStrategy,
  ViewChildren,
  inject,
} from '@angular/core';
import { cloneDeep } from 'lodash';
import {
  ModalRef,
  BackendResponse,
  FileUploaderV2,
  FileV2Api,
  deepClone,
  WorkflowItem,
  ReactiveComponent,
  ModalModule,
  WorkflowModule,
  NotificationsV2Module,
  ButtonV2Module,
} from '@schenck/rotec-components';
import { BehaviorSubject, Observable, Subject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, tap, switchMap, takeUntil, filter } from 'rxjs/operators';
import { MachineTaskInfo } from 'src/api';
import { Components, isComponentEnum } from 'src/components/components-and-function-group';
import { SchedulingModel } from 'src/components/scheduling/scheduling.component';
import { MachineTaskService, MachinesSearchService } from 'src/services';
import { MACHINE_WORKFLOW_ITEMS } from '../../../utils/constants/upsert-task.const';
import { StepComponent } from './steps/machine-step-base.component';
import { DurationUnit } from 'src/utils/constants/duration.const';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { MachineStepComponent } from './steps/machine-step/machine-step.component';
import { MachineDescriptionStepComponent } from './steps/description-step/description-step.component';
import { MachineSchedulingStepComponent } from './steps/scheduling-step/scheduling-step.component';
import { MachineDocumentsStepComponent } from './steps/documents-step/documents-step.component';
import { TranslatePipe } from '@ngx-translate/core';

export interface MachineTaskUpsertModel extends MachineTaskInfo {
  __scheduling?: SchedulingModel;
  customComponent?: string;
  /**
   * The Duration Unit which is selected (UI only)
   * This Unit must be converted back to Minutes in the request
   */
  expectedDurationUnit?: DurationUnit;
}

@Component({
  selector: 'ma-upsert-machine-task-wizard',
  templateUrl: './upsert-machine-task-wizard.component.html',
  styleUrls: ['./upsert-machine-task-wizard.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MachinesSearchService],
  imports: [
    ModalModule,
    WorkflowModule,
    NotificationsV2Module,
    MachineStepComponent,
    MachineDescriptionStepComponent,
    MachineSchedulingStepComponent,
    MachineDocumentsStepComponent,
    ButtonV2Module,
    NgTemplateOutlet,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class UpsertMachineTaskWizardComponent
  extends ReactiveComponent
  implements AfterViewInit, OnDestroy
{
  private readonly modalRef =
    inject<ModalRef<MachineTaskInfo, (MachineTaskInfo | null)[]>>(ModalRef);
  private readonly taskService = inject(MachineTaskService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fileApi = inject(FileV2Api);
  public machinesSearchService = inject(MachinesSearchService);

  @ViewChildren('steps') public steps: QueryList<StepComponent>;

  public machineTaskModel: MachineTaskUpsertModel = <any>{};
  public workflowItems = deepClone([...MACHINE_WORKFLOW_ITEMS]);
  public activeStep: WorkflowItem = this.workflowItems[0];
  public nextClick$ = new Subject<void>();
  public previousClick$ = new Subject<void>();
  public loading$ = new BehaviorSubject<boolean>(false);
  public descriptionStepIndex = 1;
  public backendError = '';

  private fileUploader = new FileUploaderV2(this.fileApi);
  private stepFormChangesSubscription: Subscription;
  private _stepState: { first: boolean; last: boolean } = { first: false, last: false };

  public get edit(): boolean {
    return !!this.modalRef.data;
  }

  public get activeItemIndex(): number {
    return this.workflowItems.findIndex(({ title }) => this.activeStep.title === title);
  }

  public get createButton(): boolean {
    return this._stepState.last && this.activeItemIndex === this.workflowItems.length - 1;
  }

  public get nextButtonDisabled(): boolean {
    return !this.isCurrentStepValid;
  }

  public get previousButtonDisabled(): boolean {
    return this.activeItemIndex === 0;
  }

  public get isCurrentStepValid(): boolean {
    return this.steps?.first?.valid;
  }

  public get leftSaveButtonActive(): boolean {
    return this.edit && !this._stepState.last && this.workflowItems.every(({ valid }) => valid);
  }

  constructor() {
    super();
    if (this.edit) {
      this.machineTaskModel = this.transformToUpsertModel(cloneDeep(this.modalRef.data));
      this.machinesSearchService.setSelected([this.modalRef.data.machineId]);
      this.workflowItems = this.workflowItems.map((item, i) => ({
        ...item,
        valid: true,
        status: i === this.descriptionStepIndex ? 'active' : 'visited',
      }));
      this.activeStep = this.workflowItems[this.descriptionStepIndex];
    }
  }

  public ngAfterViewInit(): void {
    this.steps.changes.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      // Unsubscribes from previous step form status changes
      this.stepFormChangesSubscription?.unsubscribe();
      this.updateWorkflowItemValidity();
      this.subscribeToStepFormStatusChanges();
      this.cdr.detectChanges();
    });
    this.subscribeToStepFormStatusChanges();
    this.cdr.detectChanges();
  }

  public ngOnDestroy(): void {
    this.fileUploader.cancelFiles$().subscribe();
    this.nextClick$.complete();
    this.previousClick$.complete();
    this.loading$.complete();
    this.stepFormChangesSubscription?.unsubscribe();
  }

  public stepChanged(step: WorkflowItem): void {
    this.activeStep = step;
    this.setStepState();
    this.updateWorkflowItemValidity();
  }

  public setStepState(): void {
    this._stepState.last = this.activeItemIndex === this.workflowItems.length - 1;
    this._stepState.first = this.activeItemIndex === 0;
  }

  public close(): void {
    this.modalRef.close();
  }

  public submitMachineTask(): void {
    this.backendError = '';
    this.loading$.next(true);
    let machineIds = this.machinesSearchService.state.selectedIds;

    const upsertAllTasks$ = machineIds.map((machineId) => {
      return this.upsertTask$(machineId).pipe(
        filter((result) => BackendResponse.isSuccess(result)),
        tap(() => (machineIds = machineIds.filter((id) => id !== machineId)))
      );
    });

    forkJoin(upsertAllTasks$).subscribe((results) => {
      this.loading$.next(false);
      const firstError = results.find((res) => !BackendResponse.isSuccess(res)) as any;

      if (firstError) {
        this.backendError = BackendResponse.isBadRequest(firstError)
          ? firstError.msg
          : 'TECHNICAL_ERROR_try again later or contact admin';
        // Remove from selected machines those for which it worked
        this.machinesSearchService.setSelected(machineIds);
      } else {
        this.modalRef.emitAndClose(
          (<BackendResponse.Success<MachineTaskInfo | null>[]>results).map((res) => res.result)
        );
      }
    });
  }

  private updateWorkflowItemValidity(): void {
    this.workflowItems[this.activeItemIndex].valid = this.isCurrentStepValid;
  }

  private subscribeToStepFormStatusChanges(): void {
    this.stepFormChangesSubscription = this.steps.first.stepForm.statusChanges.subscribe(() =>
      this.updateWorkflowItemValidity()
    );
  }

  private upsertTask$(machineId: string): Observable<any> {
    const model: MachineTaskInfo = {
      ...this.transformBackIntoRealModel(),
      machineId,
    };
    const upsert$ = model.id ? this.taskService.update$(model) : this.taskService.create$(model);
    return this.fileUploader.upsertFiles$().pipe(
      switchMap(() => upsert$),
      catchError(() => of(BackendResponse.backendError(null)))
    );
  }

  private transformBackIntoRealModel(): MachineTaskInfo {
    let model: MachineTaskUpsertModel = {
      ...this.machineTaskModel,
    };
    // merge scheduling
    if (model.__scheduling.single === true) {
      model.dueDate = model.__scheduling.dueDate;
      model.scheduling = null;
    } else {
      model = { ...model, ...model.__scheduling };
    }

    if (model.expectedDurationUnit === DurationUnit.H) {
      model.expectedDurationMinutes = model.expectedDurationMinutes * 60;
    }

    delete model.__scheduling;
    delete model.expectedDurationUnit;
    // merge customComponent if needed
    if (model.component === Components.OtherComponent && model.customComponent) {
      model.component = model.customComponent;
    }
    delete model.customComponent;

    return model;
  }

  private transformToUpsertModel(model: MachineTaskInfo): MachineTaskUpsertModel {
    const upsertModel: MachineTaskUpsertModel = {
      ...model,
    };
    // Add scheduling
    upsertModel.__scheduling = !!model.scheduling
      ? { single: false, scheduling: model.scheduling }
      : { single: true, dueDate: model.dueDate };
    // Add custom component
    upsertModel.customComponent = !isComponentEnum(model.component) ? model.component : null;
    upsertModel.component = !isComponentEnum(model.component)
      ? Components.OtherComponent
      : model.component;

    return upsertModel;
  }
}
